import React from "react";
import { Offcanvas, Form, Button } from "react-bootstrap";
import Select from "react-select";
import { usePermissions } from "../../../hooks/usePermissions";
import { useDebounce } from "../../../hooks/useDebounce";
import { useScopeTypes } from "../../../hooks/useScopeTypes";
import RolePermissionsSection from "../molecules/RolePermissionsSection";
import { useCreateRoleDrawer } from "./useCreateRoleDrawer";
import "./CreateRoleDrawer.css";

export interface RoleFormData {
  id: string;
  code: string;
  name: string;
  scopeType: string;
  description: string;
  permissionCodes: string[];
  permissionsCount?: number;
  createdBy?: string;
}

export type RoleDrawerMode = "create" | "edit" | "view";

interface CreateRoleDrawerProps {
  show: boolean;
  onHide: () => void;
  mode?: RoleDrawerMode;
  initialData?: RoleFormData;
  onSubmitSuccess?: () => void;
}

const CreateRoleDrawer: React.FC<CreateRoleDrawerProps> = ({
  show,
  onHide,
  mode = "create",
  initialData,
  onSubmitSuccess,
}) => {
  const { scopeTypeOptions } = useScopeTypes();
  const { functions, values, loading } = useCreateRoleDrawer({
    show,
    onHide,
    mode,
    initialData,
    onSubmitSuccess,
  });
  const debouncedPermissionSearch = useDebounce(values.permissionSearch, 600);
  const { permissions, loading: permissionsLoading } = usePermissions(
    debouncedPermissionSearch
  );

  // Build the permissions list to show in the drawer.
  // - View mode: show only permissions actually mapped to the role.
  // - Edit mode: ensure all mapped permissions are present in the list,
  //   even if they are not in the first page of the global permissions API.
  const rolePermissionItems =
    values.rolePermissions.length > 0
      ? values.rolePermissions.map((perm) => ({
          id: perm._id,
          code: perm.code,
          description: perm.description || perm.code,
        }))
      : [];

  // Final list used in the UI:
  // - View: only mapped permissions, in any order.
  // - Edit: all global permissions + any missing mapped ones,
  //         but sorted so that mapped permissions (initially assigned)
  //         appear at the top once, and stay there even if unchecked.
  let effectivePermissions = permissions;

  if (values.isView && rolePermissionItems.length > 0) {
    effectivePermissions = rolePermissionItems;
  } else if (!values.isView && rolePermissionItems.length > 0) {
    const existingCodes = new Set(permissions.map((p) => p.code));
    const hasSearch = values.permissionSearch.trim().length > 0;
    const missingFromGlobal = hasSearch
      ? []
      : rolePermissionItems.filter((p) => !existingCodes.has(p.code));

    const merged = [...permissions, ...missingFromGlobal];
    const mappedCodes = new Set(rolePermissionItems.map((p) => p.code));

    effectivePermissions = merged.slice().sort((a, b) => {
      const aMapped = mappedCodes.has(a.code);
      const bMapped = mappedCodes.has(b.code);
      if (aMapped && !bMapped) return -1;
      if (!aMapped && bMapped) return 1;
      return a.code.localeCompare(b.code);
    });
  }

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="create-role-drawer"
      style={{ width: 640, maxWidth: "100%" }}
    >
      <div className="create-role-drawer-header">
        <h2 className="create-role-drawer-title">{values.drawerTitle}</h2>
        <button
          type="button"
          className="create-role-drawer-close btn-close"
          onClick={onHide}
          aria-label="Close"
        />
      </div>
      <Offcanvas.Body className="create-role-drawer-body">
        <div className="create-role-form">
          <div className="create-role-fields">
            <div className="create-role-fields-row">
              <Form.Group className="create-role-field">
              <Form.Label>
                  Role Code (Unique) {!values.isView && <span className="required">*</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={values.roleCode}
                  onChange={(e) => functions.handleRoleCodeChange(e.target.value)}
                  className="create-role-input"
                  readOnly={!!values.isEdit || !!values.isView}
                  tabIndex={values.isEdit || values.isView ? -1 : 0}
                />
                {values.fieldErrors?.roleCode && !values.isView && (
                  <div className="text-danger small mt-1">
                    {values.fieldErrors.roleCode}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="create-role-field">
              <Form.Label>
                  Scope Type
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={
                    values.scopeType
                      ? scopeTypeOptions.find(
                          (opt) => opt.value === values.scopeType
                        ) ?? null
                      : null
                  }
                  onChange={(option) =>
                    functions.handleScopeTypeChange(option?.value ?? "")
                  }
                  options={scopeTypeOptions}
                  isDisabled={!!values.isView}
                  placeholder="Select scope type"
                  isClearable
                />
              </Form.Group>
            </div>
            <Form.Group className="create-role-field">
              <Form.Label>
                Description
              </Form.Label>
              <Form.Control
                type="text"
                value={values.description}
                onChange={(e) => functions.handleDescriptionChange(e.target.value)}
                className="create-role-input"
                readOnly={!!values.isView}
                tabIndex={values.isView ? -1 : 0}
              />
            </Form.Group>
          </div>

          <RolePermissionsSection
            permissions={effectivePermissions}
            selectedCodes={values.selectedPermissions}
            readOnly={!!values.isView}
            searchValue={values.isView ? "" : values.permissionSearch}
            onSearchChange={
              values.isView ? undefined : functions.handlePermissionSearchChange
            }
            onToggle={values.isView ? undefined : functions.handlePermissionToggle}
            instanceId="role"
            loading={values.isView ? values.isLoadingRole : permissionsLoading}
          />
        </div>
      </Offcanvas.Body>
      {!values.isView && (
        <div className="create-role-drawer-footer">
          {values.submitError && (
            <div className="create-role-error text-danger small mb-2">
              {values.submitError}
            </div>
          )}
          <Button
            variant="outline-dark"
            className="create-role-btn create-role-btn-cancel"
            onClick={onHide}
            disabled={loading.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="create-role-btn create-role-btn-submit"
            onClick={functions.handleSubmit}
            disabled={loading.isSubmitting}
          >
            {loading.isSubmitting ? "Creating..." : values.isEdit ? "Update Role" : "Create Role"}
          </Button>
        </div>
      )}
    </Offcanvas>
  );
};

export default CreateRoleDrawer;
